import { useQuery } from "@tanstack/react-query";

import { api } from "@utils/api";

import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Pagination } from "@customTypes/index";
import { SlotTemplate } from "@customTypes/slotTemplate";

import { slotTemplateQKeys } from "./queryKeys";


const select = (res: any) => {
  console.log("[Slot Templates API Response]", res.data);

  const pagination: Partial<Pagination> = {
    page: res.data.pagination?.page || res.data.page,
    pageSize: res.data.pagination?.page_size || res.data.page_size,
    pageCount: res.data.pagination?.page_count || res.data.page_count,
    total: res.data.pagination?.total || res.data.total,
  };

  const newData = [] as SlotTemplate[];

  // Handle both 'items' and 'data' array from API
  const items = res.data.items || res.data.data || [];

  if (items.length) {
    for (const item of items) {
      console.log("[Processing template]", item);

      // Map daily_schedule - ensure it's not empty
      const dailySchedule: any = {};
      if (item.daily_schedule && Object.keys(item.daily_schedule).length > 0) {
        for (const [day, blocks] of Object.entries(item.daily_schedule)) {
          dailySchedule[Number(day)] = (blocks as any[]).map((block: any) => ({
            startTime: block.start_time,
            endTime: block.end_time,
            slotDurationMinutes: block.slot_duration_minutes,
            bufferTimeMinutes: block.buffer_time_minutes,
          }));
        }
      } else {
        console.warn("[No daily_schedule found for template]", item._id);
      }

      newData.push({
        id: item._id,
        name: item.name,
        description: item.description,
        doctorAssigned: item.doctor_assigned,
        doctorName: item.doctor_name,
        dailySchedule,
        recurrence: {
          type: item.recurrence.type,
          daysOfWeek: item.recurrence.days_of_week,
          startDate: item.recurrence.start_date,
          endDate: item.recurrence.end_date,
        },
        slotConfig: {
          quantity: item.slot_config.quantity,
          defaultBooked: item.slot_config.default_booked,
          bookingLeadTimeMinutes: item.slot_config.booking_lead_time_minutes,
        },
        exceptions: item.exceptions?.map((exc: any) => ({
          id: exc._id,
          date: exc.date,
          startDate: exc.start_date,
          endDate: exc.end_date,
          reason: exc.reason,
          allDay: exc.all_day,
          timeRanges: exc.time_ranges,
          customSlots: exc.custom_slots?.map((cs: any) => ({
            startTime: cs.start_time,
            endTime: cs.end_time,
            quantity: cs.quantity,
            notes: cs.notes,
          })),
        })) || [],
        isActive: item.is_active,
        generationHorizonDays: item.generation_horizon_days,
        lastGeneratedDate: item.last_generated_date,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        createdBy: item.created_by,
      });
    }
    console.log("[Mapped templates]", newData);
    return { newData, pagination };
  }

  console.log("[No templates found in response]");
  return { newData: [], pagination };
};

interface QueryProps {
  isEnabled?: boolean;
  doctorAssigned?: string;
  isActive?: boolean;
  pagination?: Partial<Pagination>;
}

export const useGetSlotTemplates = ({
  doctorAssigned,
  isActive,
  isEnabled = true,
  pagination,
}: QueryProps) => {
  const handleErrors = useQueryErrorHandling();

  return useQuery({
    queryKey: slotTemplateQKeys.allWithFilters({
      doctorAssigned,
      isActive,
      pagination,
    }),
    queryFn: () => {
      return api.get("slot-templates/", {
        params: {
          ...(doctorAssigned && { doctor_assigned: doctorAssigned }),
          ...(typeof isActive !== "undefined" && { is_active: isActive }),
          ...(pagination && {
            page: pagination.page,
            page_size: pagination.pageSize,
          }),
        },
      });
    },
    select,
    enabled: isEnabled,
    staleTime: Infinity,

    onError: (res: any) => {
      console.error("[From Get Slot Templates]", res);
      handleErrors(res, "query");
    },
  });
};
